import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { getDepartmentList } from "../../../services/departmentList";
import { getDepartmentDetail } from "../../../services/departmentDetail";

const DepartmentDetailContext = createContext();

const initialState = {
  departments: [],
  meta: null,
  departmentDetail: null,
  loading: false,
  loadingDetail: false,
  error: null,
  isEmpty: false,
};

export const DepartmentDetailProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchDepartments = useCallback(
    async (params = {}) => {
      updateState({ loading: true, error: null, isEmpty: false });

      try {
        const requestParams = {
          page: params.page || 1,
          page_size: params.page_size || 20,
          order_by: params.order_by || "created_at",
          order_direction: params.order_direction || "DESC",
        };

        if (params.search && params.search.keyword) {
          requestParams.search = params.search;
        }

        if (params.department_uuid) {
          requestParams.department_uuid = params.department_uuid;
        }

        const response = await getDepartmentList(requestParams);

        if (response?.status_code !== 200) {
          throw new Error(
            `API returned status code ${response?.status_code}`
          );
        }

        const body = response.body || [];
        const isEmpty = !Array.isArray(body) || body.length === 0;

        updateState({
          departments: Array.isArray(body) ? body : [],
          meta: response.meta || null,
          loading: false,
          error: null,
          isEmpty,
        });
      } catch (err) {
        console.error("Error fetching departments:", err);
        updateState({
          loading: false,
          error: err,
          isEmpty: false,
        });
      }
    },
    [updateState]
  );

  const fetchDepartmentDetail = useCallback(
    async (department_uuid) => {
      if (!department_uuid) {
        updateState({ loadingDetail: false, error: new Error("department_uuid is required") });
        return;
      }

      updateState({ loadingDetail: true, error: null });

      try {
        const response = await getDepartmentDetail(department_uuid);

        if (response?.status_code !== 200) {
          throw new Error(
            `API returned status code ${response?.status_code}`
          );
        }

        updateState({
          departmentDetail: response.body || null,
          loadingDetail: false,
          error: null,
        });
      } catch (err) {
        console.error("Error fetching department detail:", err);
        updateState({
          loadingDetail: false,
          error: err,
          departmentDetail: null,
        });
      }
    },
    [updateState]
  );

  const clearDepartments = useCallback(() => {
    setState(initialState);
  }, []);

  const clearDepartmentDetail = useCallback(() => {
    setState((prev) => ({ ...prev, departmentDetail: null }));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      fetchDepartments,
      fetchDepartmentDetail,
      clearDepartments,
      clearDepartmentDetail,
    }),
    [state, fetchDepartments, fetchDepartmentDetail, clearDepartments, clearDepartmentDetail]
  );

  return (
    <DepartmentDetailContext.Provider value={value}>
      {children}
    </DepartmentDetailContext.Provider>
  );
};

export const useDepartmentDetail = () => {
  const context = useContext(DepartmentDetailContext);
  if (!context) {
    throw new Error(
      "useDepartmentDetail must be used within DepartmentDetailProvider"
    );
  }
  return context;
};

